'use client';

import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import {
  getLaunchpoolApi,
  postLaunchPoolStakeApi,
  postLaunchPoolUnstakeApi,
  postLaunchpoolAutoConversionApi,
} from '@flipster/apis/v1/launchpool';
import { Launchpool, LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import Num from '@flipster/utils/num';

import { authBunja } from '../auth/state';

export const launchpoolBunja = bunja(() => {
  const { isLoggedInAtom } = bunja.use(authBunja);
  const getLaunchpoolApiAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: ['getLaunchpoolApi', isLoggedIn],
      queryFn: getLaunchpoolApi,
    };
  });

  const launchpoolApiDataAtom = atom((get) => {
    return get(getLaunchpoolApiAtom).data?.data;
  });

  const launchpoolApiIsLoadingAtom = atom((get) => {
    return get(getLaunchpoolApiAtom).isLoading;
  });

  const launchpoolListAtom = launchpoolApiDataAtom;

  const launchpoolRecordAtom = atom((get) => {
    const launchpoolList = get(launchpoolListAtom);
    if (!launchpoolList) return {};
    const result: Record<number, Launchpool> = {};
    for (const pool of launchpoolList.pools) result[pool.id] = pool;
    return result;
  });

  const launchpoolIdAndIsSparksAtom = atom((get) => {
    const launchpoolList = get(launchpoolListAtom);
    if (!launchpoolList) return [];
    return launchpoolList.pools.map(({ id, isSparkLaunchpool }) => ({ id, isSparkLaunchpool }));
  });

  const ongoingSparkLaunchpoolExistsAtom = atom((get) => {
    const launchpoolList = get(launchpoolListAtom);
    if (!launchpoolList) return false;
    return launchpoolList.pools.some((pool) => pool.status === LaunchpoolStatus.Ongoing && pool.isSparkLaunchpool);
  });

  const postLaunchPoolStakeApiMutationAtom = atomWithMutation((get) => ({
    mutationKey: ['postLaunchPoolStakeApi'],
    mutationFn: postLaunchPoolStakeApi,
    onSuccess() {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getLaunchpoolApi'] });
      queryClient.invalidateQueries({ queryKey: ['getUserPointApi'] });
    },
  }));

  const postLaunchPoolUnstakeApiMutationAtom = atomWithMutation((get) => ({
    mutationKey: ['postLaunchPoolUnstakeApi'],
    mutationFn: postLaunchPoolUnstakeApi,
    onSuccess() {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getLaunchpoolApi'] });
    },
  }));

  const postLaunchpoolAutoConversionMutationAtom = atomWithMutation((get) => ({
    mutationKey: ['postLaunchpoolAutoConversionApi'],
    mutationFn: postLaunchpoolAutoConversionApi,
    onSuccess() {
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getLaunchpoolApi'] });
    },
  }));

  const maximumAprAtom = atom((get) => {
    const launchpoolList = get(launchpoolListAtom);
    if (!launchpoolList) return undefined;
    const pools = launchpoolList.pools.filter((pool) =>
      [LaunchpoolStatus.Ongoing, LaunchpoolStatus.Upcoming].includes(pool.status),
    );
    const maximumAprs = pools.map((pool) => {
      const maxMultiplier = pool.multipliers.reduce((max, multiplier) => Math.max(max, multiplier.multiplier), 1);
      return pool.estimatedApr
        ? Num(pool.estimatedApr).multipliedBy(maxMultiplier).toNumber()
        : Num(pool.maximumUsdtApr ?? 0).toNumber();
    });
    return Math.max(...maximumAprs, 0);
  });

  return {
    launchpoolListAtom,
    launchpoolApiIsLoadingAtom,
    launchpoolIdAndIsSparksAtom,
    launchpoolRecordAtom,
    ongoingSparkLaunchpoolExistsAtom,
    postLaunchPoolUnstakeApiMutationAtom,
    postLaunchPoolStakeApiMutationAtom,
    postLaunchpoolAutoConversionMutationAtom,
    maximumAprAtom,
  };
});

export const LaunchpoolItemIdContext = createContext<number>(NaN);
export const LaunchpoolItemIdScope = createScopeFromContext(LaunchpoolItemIdContext);

export const launchpoolItemBunja = bunja(() => {
  const launchpoolBunjaInstance = bunja.use(launchpoolBunja);
  const id = bunja.use(LaunchpoolItemIdScope);
  const { launchpoolRecordAtom } = launchpoolBunjaInstance;
  const launchpoolItemAtom = atom((get) => {
    const launchpoolRecord = get(launchpoolRecordAtom);
    const launchpoolItem = launchpoolRecord[id];
    if (!launchpoolItem) throw new Error(`launchpoolItem with id ${id} is not found`);
    return launchpoolItem;
  });
  return { launchpoolItemAtom };
});

export const useLaunchpool = () => {
  const {
    launchpoolListAtom,
    postLaunchPoolUnstakeApiMutationAtom,
    postLaunchPoolStakeApiMutationAtom,
    postLaunchpoolAutoConversionMutationAtom,
    maximumAprAtom,
  } = useBunja(launchpoolBunja);
  const launchpoolList = useAtomValue(launchpoolListAtom);
  const postLaunchPoolUnstakeApiMutation = useAtomValue(postLaunchPoolUnstakeApiMutationAtom);
  const postLaunchPoolStakeApiMutation = useAtomValue(postLaunchPoolStakeApiMutationAtom);
  const postLaunchpoolAutoConversionMutation = useAtomValue(postLaunchpoolAutoConversionMutationAtom);
  const maximumApr = useAtomValue(maximumAprAtom);

  return {
    launchpoolList,
    postLaunchPoolUnstakeApiMutation,
    postLaunchPoolStakeApiMutation,
    postLaunchpoolAutoConversionMutation,
    maximumApr,
  };
};
